import React from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Text } from 'rebass/styled-components';
import { Check } from 'lucide-react';
import { Button } from '@getro/rombo';

import { trackEvent } from '../../../helpers/analytics';

const FeatureIntro = ({
  featureName,
  title,
  description,
  features,
  ctaText,
  ctaLink,
  footerNote,
  testimonial,
  image,
  imageAlt,
}) => (
  <Flex
    sx={{
      px: '16px',
      width: '100%',
      maxWidth: '1200px',
      flexDirection: 'row',
      gap: '40px',
      alignItems: 'flex-start',
      my: '64px',
      mx: 'auto',
      position: 'relative',
    }}
  >
    <Box sx={{ maxWidth: '440px', width: '100%', flexShrink: 0 }}>
      <Flex sx={{ flexDirection: 'column', gap: '24px', alignItems: 'flex-start' }}>
        <Flex flexDirection="column" gap="8px">
          <Text
            sx={{
              color: 'text.subtle',
              fontSize: '15px',
              fontWeight: '400',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              display: 'flex',
              gap: '4px',
            }}
          >
            <Box
              as="span"
              sx={{
                background: 'linear-gradient(94deg, #4900FF 3.21%, #D400FF 63.82%, #FF9257 134.26%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: '700',
                letterSpacing: '0.5px',
              }}
            >
              {featureName}
            </Box>
          </Text>
          <Text color="text.dark" fontSize="28px" fontWeight="700" lineHeight="1.2">
            {title}
          </Text>
        </Flex>
        <Text color="text.main" fontSize="18px" fontWeight="400">
          {description}
        </Text>
        {features?.length > 0 && (
          <Flex sx={{ flexDirection: 'column', gap: '16px', color: 'text.dark', fontWeight: 500 }}>
            {features.map((item) => (
              <Flex key={item} sx={{ flexDirection: 'row', gap: '8px' }}>
                <Box as={Check} size="16px" sx={{ color: 'purple.500', flexShrink: 0, mt: '4px' }} />
                <Text>{item}</Text>
              </Flex>
            ))}
          </Flex>
        )}
      </Flex>
      <Flex sx={{ flexDirection: 'column', gap: '64px', alignItems: 'center', width: '100%', mt: '40px' }}>
        <Flex sx={{ flexDirection: 'column', gap: '8px', width: '100%' }}>
          <Button
            as="a"
            href={ctaLink}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              justifyContent: 'center',
              width: '100%',
            }}
            onClick={() => {
              trackEvent('upsell:demo_click', { featureName });
            }}
          >
            {ctaText}
          </Button>
          {footerNote && (
            <Text color="text.main" fontSize="14px" fontWeight="400" textAlign="center">
              {footerNote}
            </Text>
          )}
        </Flex>

        {testimonial && (
          <Flex sx={{ justifyContent: 'space-between', gap: '16px', width: '100%' }}>
            {testimonial.avatarSrc && (
              <Box sx={{ position: 'relative', width: '64px', height: '64px', flexShrink: 0 }}>
                <Box
                  sx={{
                    backgroundImage: `url(${testimonial.avatarSrc})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    width: '64px',
                    height: '64px',
                    borderRadius: '64px',
                  }}
                />
                <Box
                  sx={{
                    background: 'linear-gradient(219deg, #702DFF 15.72%, #F4F 73.31%)',
                    opacity: 0.7,
                    mixBlendMode: 'lighten',
                    width: '64px',
                    height: '64px',
                    borderRadius: '64px',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                  }}
                />
              </Box>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Box
                sx={{
                  borderRadius: '4px',
                  bg: 'neutral.20',
                  p: '16px',
                  width: '320px',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: '-6px',
                    top: '16px',
                    width: 0,
                    height: 0,
                    borderTop: '6px solid transparent',
                    borderBottom: '6px solid transparent',
                    borderRight: '6px solid',
                    borderRightColor: 'neutral.20',
                  },
                }}
              >
                <Text fontSize="16px" fontWeight="400" color="text.dark">
                  {testimonial.quote}
                </Text>
                <Box mt="16px">
                  <Text sx={{ color: 'text.dark', fontWeight: 600, fontSize: '14px' }}>{testimonial.author}</Text>
                  <Text sx={{ color: 'text.subtle', fontWeight: 400, fontSize: '14px' }}>{testimonial.role}</Text>
                </Box>
              </Box>
            </Box>
          </Flex>
        )}
      </Flex>
    </Box>

    {image && (
      <Box
        sx={{
          flex: 1,
          textAlign: 'center',
          position: 'relative',
          minHeight: '500px',
          minWidth: 0,
          width: '100%',
          display: ['none', 'none', 'block'],
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: '328px',
            height: '327px',
            borderRadius: '50%',
            background: '#6D33FF',
            opacity: 0.4,
            filter: 'blur(80px)',
            top: '20%',
            left: '0%',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: '345px',
            height: '338px',
            borderRadius: '50%',
            background: '#FA816B',
            opacity: 0.2,
            filter: 'blur(100px)',
            top: '5%',
            right: '0%',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: '415px',
            height: '414px',
            borderRadius: '50%',
            background: '#E77DFC',
            opacity: 0.3,
            filter: 'blur(100px)',
            top: '40%',
            right: '-10%',
          }}
        />
        <Box
          as="img"
          src={image}
          alt={imageAlt}
          sx={{
            position: 'relative',
            mx: 'auto',
            zIndex: 1,
            width: '100%',
            maxWidth: '630px',
            height: 'auto',
          }}
        />
      </Box>
    )}
  </Flex>
);

FeatureIntro.propTypes = {
  featureName: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  features: PropTypes.arrayOf(PropTypes.string),
  ctaText: PropTypes.string,
  ctaLink: PropTypes.string,
  footerNote: PropTypes.string,
  testimonial: PropTypes.shape({
    quote: PropTypes.string.isRequired,
    author: PropTypes.string.isRequired,
    role: PropTypes.string.isRequired,
    avatarSrc: PropTypes.string,
  }),
  image: PropTypes.string,
  imageAlt: PropTypes.string,
};

FeatureIntro.defaultProps = {
  features: [],
  footerNote: null,
  testimonial: null,
  image: null,
  imageAlt: '',
  ctaText: 'Book a demo',
  ctaLink: 'https://www.getro.com/demo?utm_source=getro',
};

export default FeatureIntro;
